import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom'; // 引入 NavLink
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { isAuthProviderEnabled, isWeb3Enabled, formatWalletAddress } from '../../utils/auth';
import { UserMenu } from './UserMenu';
import { FlatworldIcon } from '../common/FlatworldIcon';

export const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, disconnectWallet } = useAuth();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await disconnectWallet();
    } catch (error) {
      console.error('登出失败:', error);
    }
  };

  const isAuthenticated = !!user;
  const userProfile = user ? {
    name: user.profile.name,
    avatar: user.profile.avatar,
    walletType: user.type,
    address: user.address
  } : null;

  return (
    <nav className="bg-white/80 backdrop-blur-sm border-b border-neutral-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <FlatworldIcon className="w-8 h-8" />
              </Link>
            </div>
            {/* 使用 NavLink 替代 Link，并使用 className 函数 */}
            <div className="hidden sm:ml-8 sm:flex sm:items-center sm:space-x-8">
              <NavLink
                to="/"
                end // 添加 end 属性确保精确匹配根路径
                className={({ isActive }) =>
                  `inline-flex items-center px-2 pt-1 border-b-2 text-sm transition-colors ${
                    isActive
                      ? 'border-[#5aaf8c] text-[#2f7057] font-medium' // 使用项目颜色
                      : 'border-transparent text-neutral-700 hover:text-[#2f7057] hover:border-[#a3d9c0]'
                  }`
                }
              >
                {t('nav.home')}
              </NavLink>

              {/* 游戏菜单项 - 暂时注释掉
              <NavLink
                to="/story"
                 className={({ isActive }) =>
                  `inline-flex items-center px-2 pt-1 border-b-2 text-sm transition-colors ${
                    isActive
                      ? 'border-[#5aaf8c] text-[#2f7057] font-medium'
                      : 'border-transparent text-neutral-700 hover:text-[#2f7057] hover:border-[#a3d9c0]'
                  }`
                }
              >
                {t('nav.game')}
              </NavLink>
              */}
              {/* DB Chain菜单项 - 暂时注释掉
              <NavLink
                to="/db_chain"
                 className={({ isActive }) =>
                  `inline-flex items-center px-2 pt-1 border-b-2 text-sm transition-colors ${
                    isActive
                      ? 'border-[#5aaf8c] text-[#2f7057] font-medium'
                      : 'border-transparent text-neutral-700 hover:text-[#2f7057] hover:border-[#a3d9c0]'
                  }`
                }
              >
                {t('nav.chain')}
              </NavLink>
              */}
              {/* Mint NFT菜单项 - 暂时注释掉
              <NavLink
                to="/character_gallery"
                 className={({ isActive }) =>
                  `inline-flex items-center px-2 pt-1 border-b-2 text-sm transition-colors ${
                    isActive
                      ? 'border-[#5aaf8c] text-[#2f7057] font-medium'
                      : 'border-transparent text-neutral-700 hover:text-[#2f7057] hover:border-[#a3d9c0]'
                  }`
                }
              >
                {t('nav.mint')}
              </NavLink>
              */}
              <NavLink
                to="https://doc.flatland.app/"
                 className={({ isActive }) =>
                  `inline-flex items-center px-2 pt-1 border-b-2 text-sm transition-colors ${
                    isActive
                      ? 'border-[#5aaf8c] text-[#2f7057] font-medium'
                      : 'border-transparent text-neutral-700 hover:text-[#2f7057] hover:border-[#a3d9c0]'
                  }`
                }
              >
                {t('nav.launcher')} {/* 项目文档 */}
              </NavLink>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <UserMenu userProfile={userProfile} onSignOut={handleSignOut} />
              </div>
            ) : (
              <>
                {isWeb3Enabled() && (
                  <Link to="/auth">
                    <Button
                      variant="accent"
                      size="sm"
                      className="flex items-center space-x-2 bg-primary-500 text-white hover:bg-primary-600 rounded-full shadow-sm"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm0 14c-2.03 0-4.43-.82-6.14-2.88a9.947 9.947 0 0112.28 0C16.43 19.18 14.03 20 12 20z"
                        />
                      </svg>
                      <span>{t('auth.connectWallet')}</span>
                    </Button>
                  </Link>
                )}
              </>
            )}

            {/* Mobile menu button */}
            <div className="flex items-center sm:hidden ml-4">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-full text-neutral-700 hover:text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                aria-controls="mobile-menu"
                aria-expanded={isMobileMenuOpen}
                onClick={toggleMobileMenu}
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {/* Mobile menu - 也使用 NavLink */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-white/95 border-t border-neutral-200 animate-slide-down shadow-md" id="mobile-menu">
          <div className="pt-2 pb-3 space-y-1">
            <NavLink
              to="/"
              end // 精确匹配
              className={({ isActive }) =>
                `block pl-3 pr-4 py-2 border-l-4 text-base ${
                  isActive
                    ? 'border-[#5aaf8c] text-[#2f7057] bg-[#edf8f3]'
                    : 'border-transparent text-neutral-700 hover:bg-neutral-50 hover:border-[#a3d9c0] hover:text-[#2f7057]'
                }`
              }
              onClick={closeMobileMenu}
            >
              {t('nav.home')}
            </NavLink>
            <NavLink
              to="https://doc.flatland.app/"
              className={({ isActive }) =>
                `block pl-3 pr-4 py-2 border-l-4 text-base ${
                  isActive
                    ? 'border-[#5aaf8c] text-[#2f7057] bg-[#edf8f3]'
                    : 'border-transparent text-neutral-700 hover:bg-neutral-50 hover:border-[#a3d9c0] hover:text-[#2f7057]'
                }`
              }
              onClick={closeMobileMenu}
              >
              {t('nav.launcher')}
            </NavLink>
            {/* 游戏菜单项 - 暂时注释掉
            <NavLink
              to="/story"
              className={({ isActive }) =>
                `block pl-3 pr-4 py-2 border-l-4 text-base ${
                  isActive
                    ? 'border-[#5aaf8c] text-[#2f7057] bg-[#edf8f3]'
                    : 'border-transparent text-neutral-700 hover:bg-neutral-50 hover:border-[#a3d9c0] hover:text-[#2f7057]'
                }`
              }
              onClick={closeMobileMenu}
              >
              {t('nav.game')}
            </NavLink>
            */}

            {/* Mint NFT菜单项 - 暂时注释掉
            <NavLink
              to="/character_gallery"
              className={({ isActive }) =>
                `block pl-3 pr-4 py-2 border-l-4 text-base ${
                  isActive
                    ? 'border-[#5aaf8c] text-[#2f7057] bg-[#edf8f3]'
                    : 'border-transparent text-neutral-700 hover:bg-neutral-50 hover:border-[#a3d9c0] hover:text-[#2f7057]'
                }`
              }
              onClick={closeMobileMenu}
              >
              {t('nav.mint')}
            </NavLink>
            */}

            {/* DB Chain菜单项 - 暂时注释掉
            <NavLink
              to="/db_chain"
              className={({ isActive }) =>
                `block pl-3 pr-4 py-2 border-l-4 text-base ${
                  isActive
                    ? 'border-[#5aaf8c] text-[#2f7057] bg-[#edf8f3]'
                    : 'border-transparent text-neutral-700 hover:bg-neutral-50 hover:border-[#a3d9c0] hover:text-[#2f7057]'
                }`
              }
              onClick={closeMobileMenu}
              >
              {t('nav.chain')}
            </NavLink>
            */}
            {!isAuthenticated && (
              <NavLink // 对于非导航项，用 Link 或 NavLink 都可以，这里保持一致
                to="/auth"
                className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base text-neutral-700 hover:bg-neutral-50 hover:border-primary-200 hover:text-primary-600"
                onClick={closeMobileMenu}
              >
                {t('auth.connectWallet')}
              </NavLink>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
